import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Instagram,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import EstimateForm from "@/components/EstimateForm";

export default function Contact() {
  const { t } = useLanguage();
  usePageMeta("contact");
  const info = t("contact.info");

  return (
    <div className="animate-fade-up">
      <section className="py-20 sm:py-28 lg:py-40 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">{t("contact.eyebrow")}</div>
          <h1 data-testid="contact-title" className="font-heading text-5xl lg:text-7xl font-normal tracking-tight leading-[1.15] text-slate-900 mt-4 leading-[1.05] max-w-4xl">
            {t("contact.title")}
          </h1>
          <p className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed">
            {t("contact.subtitle")}
          </p>
        </div>
      </section>

      <section className="py-20 sm:py-28 lg:py-40 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          {/* Info column */}
          <div className="lg:col-span-1 space-y-8">
            <InfoBlock
              icon={Mail}
              title={info.email}
              value="contact@reflectumpellicule.com"
              href="mailto:contact@reflectumpellicule.com"
              testId="contact-info-email"
            />
            <InfoBlock
              icon={Phone}
              title={info.phone}
              value="438 255 6438"
              href="tel:+14382556438"
              testId="contact-info-phone"
            />
            <InfoBlock icon={MapPin} title={info.area} value={info.areaValue} testId="contact-info-area" />
            <InfoBlock icon={Clock} title={info.hours} value={info.hoursValue} testId="contact-info-hours" />
            <InfoBlock
              icon={Instagram}
              title="Instagram"
              value="@reflectum.pellicule"
              href="https://www.instagram.com/reflectum.pellicule/"
              testId="contact-info-instagram"
            />
            <div className="p-6 rounded-2xl bg-slate-950 text-white">
              <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-400">{info.fastEyebrow}</div>
              <div className="font-heading text-xl mt-3">{info.fastTitle}</div>
              <p className="mt-2 text-slate-300 text-sm leading-relaxed">
                {info.fastText}
              </p>
            </div>
          </div>

          {/* Formulaire d'estimation */}
          <div className="lg:col-span-2">
            <div className="p-8 lg:p-10 rounded-2xl bg-slate-50 border border-slate-200">
              <EstimateForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoBlock({ icon: Icon, title, value, href, testId }) {
  const content = (
    <div data-testid={testId} className="flex items-start gap-4 group">
      <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 grid place-items-center flex-shrink-0 transition-colors group-hover:bg-blue-600 group-hover:text-white">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-slate-500">{title}</div>
        <div className="font-heading text-lg text-slate-900 mt-1">{value}</div>
      </div>
    </div>
  );
  const isExternal = href && href.startsWith("http");
  return href ? (
    <a href={href} {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
      {content}
    </a>
  ) : content;
}
